'use client';

import { Button } from '@packages/shared/src/ui/Button/Button';
import {
  LoyaltyAccountDataType,
  LoyaltyEntitlementStatus,
  useLoyaltyApi,
} from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import useSWRMutation from 'swr/mutation';
import { useContext } from 'react';
import { useSWRConfig } from 'swr';
import { usePaybackClient } from '@packages/modules/src/Payback/usePaybackClient';
import { buttonMessages } from '../Payback/messages';
import { useLoyaltyEntitlementInformation } from './useLoyaltyEntitlementInformation';
import { PaybackFetchMethodContext } from '../Payback/PaybackAccountDataFetchProvider';

const statusButtonMap: Record<LoyaltyEntitlementStatus, MessageDescriptor> = {
  [LoyaltyEntitlementStatus.Rejected]: buttonMessages.DialogDeclineButton,
  [LoyaltyEntitlementStatus.Accepted]: buttonMessages.DialogAcceptButton,
  [LoyaltyEntitlementStatus.NewVersion]: buttonMessages.DialogUpdateButton,
};

export const UpdateLoyaltyEntitlementConsentButton = ({
  status,
}: {
  status: LoyaltyEntitlementStatus;
}) => {
  const { formatMessage, locale } = useIntl();
  const { mutate } = useSWRConfig();
  const context = usePaybackClient();
  if (!context) {
    throw new Error('usePaybackClient must be used within PaybackClientProvider');
  }
  const { closeDialogHandler } = context;
  const { setFetchMethod } = useContext(PaybackFetchMethodContext);
  const { shortName } = useLoyaltyEntitlementInformation();
  const loyaltyApi = useLoyaltyApi();
  const { trigger } = useSWRMutation(
    'updateLoyaltyEntitlementConsent',
    () =>
      loyaltyApi?.updateLoyaltyEntitlementConsent({
        ecLocale: locale,
        loyaltyEntitlementItem: { shortName, status },
      }),
    // important to force a revalidation
    {
      onSuccess: () => {
        setFetchMethod(LoyaltyAccountDataType.AllUpdated);
        mutate('getLoyaltyAccountData').catch(() => undefined);
        closeDialogHandler();
      },
    },
  );
  const handleUpdateLoyaltyEntitlementConsent = async () => {
    await trigger();
  };

  return (
    <Button onClick={handleUpdateLoyaltyEntitlementConsent} color="primary">
      {formatMessage(statusButtonMap[status])}
    </Button>
  );
};
